import { useQueryClient } from '@tanstack/react-query'
import { Check, Share } from 'lucide-react'
import { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Obj, type ObjId } from '../../components/Obj.tsx'
import { ReminderTime } from '../../components/ReminderTime.tsx'
import { Sheet } from '../../components/Sheet.tsx'
import { ApiError, type Prefs } from '../../lib/api.ts'
import { needsHomeScreenForNotifications } from '../../lib/platform.ts'
import { enablePush } from '../../lib/push.ts'
import { FONT_LABELS, setFontScale, useFontScale, type FontScale } from '../../lib/prefs.ts'
import { updatePrefs, useSession } from '../../lib/queries.ts'

const JALUR: { key: Prefs['jalur']; title: string; body: string; obj: ObjId }[] = [
  { key: 'maknai', title: 'Sudah lancar', body: 'Langsung ke makna tiap ayat. Tulisan latin disembunyikan.', obj: 'mushaf' },
  { key: 'baca', title: 'Masih belajar', body: 'Ayat harian tetap jalan, dengan tulisan latin. Jalur belajar huruf menyusul.', obj: 'tunas' },
]

/** /mulai: onboarding step 1 (docs/PRD.md US-2.1–2.3). Focus screen, no tab bar; everything can change later in Pengaturan. */
export function OnboardingPage() {
  const { data } = useSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const fs = useFontScale()
  const saved = data?.state === 'ok' ? data.me.prefs : null
  const [jalur, setJalur] = useState<Prefs['jalur']>(saved?.jalur ?? 'maknai')
  const [remind, setRemind] = useState(saved?.reminder.enabled ?? true)
  const [hour, setHour] = useState(saved?.reminder.hour ?? 5)
  const [minute, setMinute] = useState(saved?.reminder.minute ?? 0)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const [iosHelp, setIosHelp] = useState(false)
  const ids = { jalur: useId(), fs: useId(), remind: useId() }

  const save = async () => {
    setIosHelp(false)
    setError('')
    setPending(true)
    try {
      await updatePrefs(qc, { jalur, fontScale: fs, latinOn: jalur === 'baca', reminder: { enabled: remind, hour, minute } })
      navigate('/mulai/rencana')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Terjadi masalah. Coba lagi nanti.')
      setPending(false)
    }
  }

  // US-2.3: the permission prompt comes only after "Lanjut" (inside this tap, as browsers require). Not awaited: a prompt
  // left unanswered must not hold the flow. A "yes" registers this browser for the reminder in the background.
  const next = async () => {
    if (remind && needsHomeScreenForNotifications()) return setIosHelp(true)
    if (remind) void enablePush()
    await save()
  }

  return (
    <main className="consent onb">
      <p className="eyebrow">Langkah 1 dari 2</p>
      <h1 style={{ marginTop: 6 }}>Atur langkahmu</h1>
      <p className="muted" style={{ marginTop: 8 }}>Tiga hal kecil supaya IqraPath pas buatmu. Semuanya bisa diubah nanti di Pengaturan.</p>

      <h2 className="section-t" id={ids.jalur}>Membaca huruf Arab</h2>
      <div className="choices" role="radiogroup" aria-labelledby={ids.jalur}>
        {JALUR.map((j) => (
          <label key={j.key} className="card choice">
            <input className="sr" type="radio" name="jalur" value={j.key} checked={jalur === j.key} onChange={() => setJalur(j.key)} />
            <Obj id={j.obj} size={52} />
            <span className="choice-t"><b>{j.title}</b><small>{j.body}</small></span>
            <span className="tick" aria-hidden="true"><Check size={16} strokeWidth={3} /></span>
          </label>
        ))}
      </div>

      <h2 className="section-t" id={ids.fs}>Ukuran huruf</h2>
      <div className="seg" role="group" aria-labelledby={ids.fs}>
        {FONT_LABELS.map((l, i) => (
          <button key={l} type="button" aria-pressed={fs === i} onClick={() => setFontScale(i as FontScale)}>
            <span style={{ fontSize: 15 + i * 3 }}>Aa</span>
            <small style={{ fontSize: '.72rem' }}>{l}</small>
          </button>
        ))}
      </div>
      <p className="help">Langsung terlihat di layar ini. Pilih yang paling nyaman dibaca tanpa menyipitkan mata.</p>

      <h2 className="section-t">Pengingat harian</h2>
      <div className="card">
        <div className="set-row">
          <div style={{ flex: 1 }}>
            <b id={ids.remind}>Ingatkan aku untuk ayat hari ini</b>
            <p className="help" style={{ marginTop: 2 }}>Satu notifikasi sehari, tidak lebih.</p>
          </div>
          <button type="button" role="switch" aria-checked={remind} aria-labelledby={ids.remind} className="switch" onClick={() => setRemind(!remind)} />
        </div>
        {remind && <ReminderTime hour={hour} minute={minute} onChange={(h, m) => { setHour(h); setMinute(m) }} />}
      </div>

      <p className="error" role="alert">{error}</p>
      <button className="btn btn-primary btn-block" type="button" onClick={next} disabled={pending} style={{ minHeight: 54 }}>
        {pending ? <><span className="spinner" aria-hidden="true" /> Menyimpan…</> : 'Lanjut'}
      </button>
      {remind && <p className="help" style={{ textAlign: 'center' }}>Setelah ini browser akan menanyakan izin notifikasi.</p>}

      {iosHelp && (
        <Sheet title="Simpan ke layar utama dulu" onClose={() => setIosHelp(false)}>
          <p className="muted">Di iPhone, pengingat hanya bisa muncul kalau IqraPath dibuka dari layar utama.</p>
          <ol className="steps">
            <li>Ketuk <Share size={16} aria-label="tombol Bagikan" /> di bawah layar Safari.</li>
            <li>Pilih <b>Tambah ke Layar Utama</b>.</li>
            <li>Buka IqraPath dari ikon barunya.</li>
          </ol>
          <p className="help">Jam pengingatmu tetap kami simpan sekarang.</p>
          <button className="btn btn-primary btn-block" type="button" onClick={save} style={{ marginTop: 14 }}>Mengerti, lanjut</button>
        </Sheet>
      )}
    </main>
  )
}
